import { isEqual } from 'lodash-es';
import { useCallback, useLayoutEffect } from 'react';
import type { MarkRequired } from 'ts-essentials';

import { useIsPageFocused } from '#@/shared/hooks/useIsPageFocused';

import type { BaseOptions, CompatibleStorageValue } from './types';
import { getValueForKey, getVersionedKey } from './utils';

interface ReadOptions<
  T extends CompatibleStorageValue,
  PersistedParsedValue extends CompatibleStorageValue = Partial<T>,
> extends MarkRequired<BaseOptions<T, PersistedParsedValue>, 'transformValueOnRead'> {
  whenRef: React.MutableRefObject<boolean>;
}

export function useReadFromLocalStorage<
  T extends CompatibleStorageValue,
  PersistedParsedValue extends CompatibleStorageValue,
>(
  unversionedKey: string,
  emptyValue: T,
  storedValue: T,
  onUpdate: (newValue: T) => void,
  {
    version,
    duration,
    whenRef,
    shouldSuspendUpdateOnPageInvisibility = false,
    parse,
    stringify,
    transformValueOnRead,
  }: ReadOptions<T, PersistedParsedValue>,
) {
  const handleStorageUpdate = useCallback(
    ({ key }: StorageEvent) => {
      const versionedKey = getVersionedKey(unversionedKey, version);
      if (key !== unversionedKey && key !== versionedKey) return;
      if (!whenRef.current) return;

      const newValue = getValueForKey<PersistedParsedValue>({
        unversionedKey,
        version,
        duration,
        parse,
        stringify,
      });

      const getValue = (untransformedValue: PersistedParsedValue | null) => {
        if (untransformedValue === null) {
          return emptyValue;
        }

        const transformedValue = transformValueOnRead(untransformedValue);
        if (transformedValue === null) {
          return emptyValue;
        }

        return transformedValue;
      };

      const value = getValue(newValue);

      // This check is critical. Without it we end up with an infinite loop in case there
      // are multiple tabs.
      if (!isEqual(value, storedValue)) {
        onUpdate(value);
      }
    },
    [
      unversionedKey,
      version,
      whenRef,
      duration,
      parse,
      stringify,
      onUpdate,
      transformValueOnRead,
      emptyValue,
      storedValue,
    ],
  );

  const isPageFocused = useIsPageFocused({
    shouldListenToWindowFocus: shouldSuspendUpdateOnPageInvisibility,
    onPageFocus: useCallback(() => {
      const newValue = getValueForKey<PersistedParsedValue>({
        unversionedKey,
        version,
        duration,
        parse,
        stringify,
      });

      const value = (newValue !== null && transformValueOnRead(newValue)) || emptyValue;
      onUpdate(value);
    }, [
      unversionedKey,
      version,
      duration,
      parse,
      stringify,
      onUpdate,
      transformValueOnRead,
      emptyValue,
    ]),
  });

  useLayoutEffect(() => {
    if (shouldSuspendUpdateOnPageInvisibility && !isPageFocused) {
      return;
    }

    window.addEventListener('storage', handleStorageUpdate);
    return () => window.removeEventListener('storage', handleStorageUpdate);
  }, [shouldSuspendUpdateOnPageInvisibility, handleStorageUpdate, isPageFocused]);
}
